import React, { ReactElement, useEffect, useRef, useState } from "react";
import styles from "./RangeFilter.module.scss";
import { Input } from "@kursk/components/ui/Input/Input";
import { CloseIconSmall, BackArrowIcon } from '@kursk/components/ui/icons';
import { Checkbox } from "@kursk/components/ui/Checkbox/Checkbox";
import InputMask from "react-input-mask";
import Slider from "rc-slider";
import classNames from "classnames";
import { getConfig } from "@root/config/config";

const config = getConfig();

interface IProps {
  rangeValues: IRange;
  setIsFree?: React.Dispatch<React.SetStateAction<boolean>>;
  isFree?: boolean;
  setWithTickets?: React.Dispatch<React.SetStateAction<boolean>>;
  withTickets?: boolean;
  setIsShowFilter?: React.Dispatch<React.SetStateAction<boolean>>;
  classNameWrapper?: string;
  isMobile?: boolean;
  getBack?: React.Dispatch<React.SetStateAction<void>>;
  close?: any;
  selectedRangeValue?: IRange | undefined;
  setSelectedRangeValue?: React.Dispatch<React.SetStateAction<IRange | undefined>>;
  updateRangeFilters?: (minRangeValue?: number, maxRangeValue?: number) => Promise<void>;
  withFreeCheckbox?: boolean;
  withTicketsCheckbox?: boolean;
  dropDownTitle?: string;
  mobileRangeModalTitle?: string,
}

export interface IRange {
  min: number;
  max: number;
}

export function RangeFilter({
  setIsShowFilter, classNameWrapper, dropDownTitle,
  isMobile, getBack, close, rangeValues, selectedRangeValue,
  setSelectedRangeValue, setIsFree, isFree, updateRangeFilters,
  setWithTickets, withTickets, withFreeCheckbox, withTicketsCheckbox, mobileRangeModalTitle
}: IProps
): ReactElement {
  const { createSliderWithTooltip } = Slider;
  const Range = createSliderWithTooltip(Slider.Range);
  const min = rangeValues.min;
  const max = rangeValues.max;
  const [maxRangeValue, setMaxRangeValue] = useState<number>(selectedRangeValue ? selectedRangeValue.max : Number(max));
  const [minRangeValue, setMinRangeValue] = useState<number>(selectedRangeValue ? selectedRangeValue.min : Number(min));
  useEffect(() => {
    setMinRangeValue(selectedRangeValue?.min ?? Number(min))
  }, [selectedRangeValue?.min]);
  useEffect(() => {
    setMaxRangeValue(selectedRangeValue?.max ?? Number(max))
  }, [selectedRangeValue?.max]);
  const [isDisabledApply, setIsDisabledApply] = useState(false);
  const minRangeValueRef = useRef(null);
  const maxRangeValueRef = useRef(null);
  const formatChars = '9999999999';
  const afterChangeHandle = (values) => {
    minRangeValueRef.current.value = values[0];
    maxRangeValueRef.current.value = values[1];
    setMaxRangeValue(maxRangeValueRef.current.value);
    setMinRangeValue(minRangeValueRef.current.value);
  }
  const onIsFreeChange = (value) => {
    if (setIsFree) {
      setIsFree(value);
      if (value) {
        minRangeValueRef.current.value = 0;
        maxRangeValueRef.current.value = 0;
        setMaxRangeValue(0);
        setMinRangeValue(0);
      } else {
        minRangeValueRef.current.value = min;
        maxRangeValueRef.current.value = max;
        setMaxRangeValue(Number(max));
        setMinRangeValue(Number(min));
      }
    }

  }

  const applyMobile = () => {
    if (minRangeValue === Number(min) && maxRangeValue === Number(max)) {
      setSelectedRangeValue(undefined);
    } else {
      setSelectedRangeValue({ min: minRangeValue, max: maxRangeValue });
    }
    getBack();
  }

  const apply = async () => {
    if (updateRangeFilters) {
      const needUpdate = (minRangeValue !== Number(min) || maxRangeValue !== Number(max))
        && (minRangeValue !== 0 || maxRangeValue !== 0);
      if (needUpdate) {
        await updateRangeFilters(minRangeValue, maxRangeValue);
      } else {
        await updateRangeFilters();
      }
    }
    setIsShowFilter(false);
  }
  const onMaxChange = (e) => {
    setMaxRangeValue(Number(e.target.value));
    setIsDisabledApply(Number(e.target.value) < Number(minRangeValue))
  }
  const onMinChange = (e) => {
    setMinRangeValue(Number(e.target.value));
    setIsDisabledApply(Number(e.target.value) > Number(maxRangeValue));
  }
  const closeRangeFilter = () => {
    if (getBack) {
      getBack();
      close();
    } else {
      setIsShowFilter(false);
    }
  }
  const clear = () => {
    minRangeValueRef.current.value = '';
    maxRangeValueRef.current.value = '';
    if (setIsFree) setIsFree(false);
    if (setWithTickets) setWithTickets(false);
    setMaxRangeValue(max);
    setMinRangeValue(min);
    setIsDisabledApply(false);
    setSelectedRangeValue(undefined);
  }
  return (
    <div className={classNames(classNameWrapper ? classNameWrapper : styles.rangeFilter)}>
      <div className={styles.rangeFilter__wrapper}>
        {isMobile && (
          <div className={styles.rangeFilter_mobileHeader}>
            <div className={styles.rangeFilter_mobileHeader_back} onClick={() => getBack()}>
              <BackArrowIcon width={18} height={13}/>
            </div>
            <div
              className={styles.rangeFilter_mobileHeader_title}>
              {mobileRangeModalTitle ? mobileRangeModalTitle : 'Цена'}
            </div>
            <div className={styles.rangeFilter_mobileHeader_close} onClick={() => closeRangeFilter()}>
              <CloseIconSmall />
            </div>
          </div>
        )}
        <div className={styles.rangeFilter__header}>
          <span className={styles.rangeFilter__header_text}>
            {dropDownTitle ? dropDownTitle : ''}
          </span>
          {!isMobile && (
            <div onClick={() => closeRangeFilter()} className={styles.rangeFilter__header_closeIconWrapper}>
              <CloseIconSmall />
            </div>
          )}
        </div>
        <div className={styles.rangeFilter__inputs}>
          <div className={styles.rangeFilter__inputs_from}>
            <span className={styles.rangeFilter__inputs_text}>От</span>
            <InputMask
              mask={formatChars}
              maskPlaceholder={null}
              onChange={(e) => onMinChange(e)}
              value={minRangeValue === min ? '' : minRangeValue.toString()}
              disabled={isFree}>
              <Input
                ref={minRangeValueRef}
                className={styles.rangeFilter__inputs_input}
                placeholder={`${min}`}
              />
            </InputMask>
          </div>
          <div className={styles.rangeFilter__inputs_to}>
            <span className={styles.rangeFilter__inputs_text}>До</span>
            <InputMask
              mask={formatChars}
              maskPlaceholder={null}
              onChange={(e) => onMaxChange(e)}
              value={maxRangeValue === max ? '' : maxRangeValue.toString()}
              disabled={isFree}>
              <Input
                ref={maxRangeValueRef}
                className={styles.rangeFilter__inputs_input}
                placeholder={`${max}`}
              />
            </InputMask>
          </div>
        </div>
        <div className={styles.rangeFilter__diapason}>
          <div className={styles.rangeFilter__diapason_wrapper}>
            <Range
              min={Number(min)}
              max={Number(max)}
              defaultValue={[Number(minRangeValue), Number(maxRangeValue)]}
              disabled={isFree}
              onAfterChange={(values) => afterChangeHandle(values)}/>
          </div>
          <div className={styles.rangeFilter__diapason_count}>
            <span className={styles.rangeFilter__diapason_text}>{min}</span>
            <span className={styles.rangeFilter__diapason_text}>{max}</span>
          </div>
        </div>
        {withFreeCheckbox && (
          <div className={styles.rangeFilter__isFree}>
            <Checkbox checked={isFree} onChange={() => onIsFreeChange(!isFree)}>
              <span className={styles.rangeFilter__isFree_text}>
                Бесплатно
              </span>
            </Checkbox>
          </div>
        )}
        {withTicketsCheckbox && config.features.events.withTicketsFilter.enabled && (
          <div className={styles.rangeFilter__withTickets}>
            <Checkbox checked={withTickets} onChange={() => setWithTickets(!withTickets)}>
              <span className={styles.rangeFilter__withTickets_text}>
                С билетами
              </span>
            </Checkbox>
          </div>
        )}
        <div className={styles.rangeFilter__divider}/>
        <div className={styles.rangeFilter__actions}>
          <button className={styles.rangeFilter__actions_clear} onClick={() => clear()}>Очистить</button>
          {!isDisabledApply && (
            <button
              className={styles.rangeFilter__actions_apply}
              onClick={() => isMobile ? applyMobile() : apply()}
            >Применить</button>
          )}
          {isDisabledApply && (
            <button className={styles.rangeFilter__actions_apply_disabled}>Применить</button>
          )}
        </div>
      </div>
    </div>
  )
}
